import React, { FormEvent, Dispatch, RefObject } from 'react';
import { Link } from 'react-router-dom';
import Banner, { Variant as BannerVariant } from '@leafygreen-ui/banner';
import Button from '@leafygreen-ui/button';
import Tooltip from '@leafygreen-ui/tooltip';
import { H2 } from '@leafygreen-ui/typography';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

// types
import { FieldNames } from 'js/auth/types/field';
import { RegexValidation } from 'js/common/types/regexValidation';
import { ErrorField, RegistrationFormFields, ReducerAction } from '../types/registration';
import { Search } from 'js/auth/types/search';
import { MarketplaceRegistrationCookie } from 'js/common/types/billing';

// utils
import { jobFunctions } from 'js/auth/utils/jobFunction';
import { countries } from 'js/common/utils/countries';
import colors from 'js/auth/utils/palette';

// styles
import { Container, FooterText, submitButton, header, ErrorMessage } from './styles/form';
import { alertBanner } from 'js/auth/components/styles/alert';
import { mq } from 'js/common/utils/mediaQueries';
import { bannerStyle } from 'js/auth/components/styles/banner';

// components
import LineDivider from 'js/common/components/LineDivider';
import FormDropdown from './FormDropdown';
import FormInput from './FormInput';
import HeaderLogo from './HeaderLogo';
import InviteBanner from './InviteBanner';
import GoogleAuthButton from './GoogleAuthButton';
import TermsOfServiceCheckbox from './TermsOfServiceCheckbox';
import ReCaptcha, { ReCaptchaHandles } from './ReCaptcha';
import PasswordInputWithValidationCriteria from './PasswordInputWithValidationCriteria';
import SocialAuthButton, { SocialAuthProvider } from './SocialAuthButton';

const marketplaceLink = css({
  color: colors.link,
});

const CheckboxWrapper = styled('div')(
  mq({
    width: ['250px', 'auto', '250px', '250px'],
  })
);

// This is a workaround because disabled buttons cannot
// have pointer events (therefore no tooltips)
const disabledButtonWrapper = css(
  mq({
    marginTop: [48, 48, 48, 48],
    display: 'inline-block',
  })
);

const submitButtonWithoutTopMargins = css(
  mq(submitButton, {
    marginTop: [0, 0, 0, 0],
  })
);

const StyledLineDivider = styled(LineDivider)(
  mq({
    width: ['300px', 'calc(100vw - 32px)', '300px', '300px'], // FormInput width
    margin: '20px 0',
  })
);

interface RegistrationFormProps extends RegistrationFormFields {
  groupname?: string;
  orgname?: string;
  isRetiredUsername: boolean;
  isInviteFlow: boolean;
  isSubmitDisabled: boolean;
  isSmallViewport: boolean;
  hasUsers?: boolean;
  isGoogleAuthEnabled: boolean;
  isSocialSignupDisabled: boolean;
  isGithubAuthEnabled: boolean;
  userRegistrationEnabled: boolean;
  onPrem?: boolean;
  fieldValidationMap: { [key in FieldNames]?: RegexValidation };
  errorField: ErrorField;
  errorMessage: string;
  inviteToken?: string;
  activationCode?: string;
  reCaptchaEnabledRegistration: boolean;
  reCaptchaRef: RefObject<ReCaptchaHandles>;
  captchaError: string;
  registrationExtraFields: Array<
    FieldNames.COMPANY | FieldNames.COUNTRY | FieldNames.JOB_RESPONSIBILITY | FieldNames.PHONE_NUMBER
  >;
  dispatch: Dispatch<ReducerAction>;
  onSubmit(e: FormEvent<HTMLFormElement>): void;
  marketplaceRegistrationCookie: MarketplaceRegistrationCookie | null;
  formSubmitted: boolean;
  search: Search;
}

export default function RegistrationForm({
  username,
  password,
  firstName,
  lastName,
  groupname,
  orgname,
  phoneNumber,
  company,
  country,
  jobResponsibility,
  tos,
  honeypot,
  isRetiredUsername,
  isInviteFlow,
  isSubmitDisabled,
  isSmallViewport,
  hasUsers,
  isGoogleAuthEnabled,
  isSocialSignupDisabled,
  isGithubAuthEnabled,
  userRegistrationEnabled,
  onPrem,
  fieldValidationMap,
  errorMessage,
  errorField,
  inviteToken,
  activationCode,
  reCaptchaEnabledRegistration,
  reCaptchaRef,
  captchaError,
  registrationExtraFields,
  dispatch,
  onSubmit,
  marketplaceRegistrationCookie,
  formSubmitted,
  search,
}: RegistrationFormProps) {
  const isPasswordError = errorField === FieldNames.PASSWORD;
  const isUsernameError = errorField === FieldNames.USERNAME;
  const isCompanyNameError = errorField === FieldNames.COMPANY;
  const isFirstNameError = errorField === FieldNames.FIRST_NAME;
  const isLastNameError = errorField === FieldNames.LAST_NAME;
  const isPhoneNumberError = errorField === FieldNames.PHONE_NUMBER;

  const isSocialAuthEnabled = isGoogleAuthEnabled || isGithubAuthEnabled;

  const extraFieldsMap = {
    phoneNumber: () => (
      <FormInput
        key="phoneNumber"
        fieldName={FieldNames.PHONE_NUMBER}
        labelName="Phone Number"
        type="tel"
        autoComplete="tel"
        onChange={(e) =>
          dispatch({ type: 'field', payload: { field: FieldNames.PHONE_NUMBER, value: e.target.value } })
        }
        value={phoneNumber}
        regexValidation={fieldValidationMap[FieldNames.PHONE_NUMBER]}
        hasError={isPhoneNumberError}
        errorMessage={errorMessage}
      />
    ),
    company: () => (
      <FormInput
        key="company"
        fieldName={FieldNames.COMPANY}
        labelName="Company Name"
        autoComplete="organization"
        hasError={isCompanyNameError}
        errorMessage={errorMessage}
        onChange={(e) => dispatch({ type: 'field', payload: { field: FieldNames.COMPANY, value: e.target.value } })}
        value={company}
      />
    ),
    jobResponsibility: () => (
      <FormDropdown
        key="jobResponsibility"
        fieldName={FieldNames.JOB_RESPONSIBILITY}
        labelName="Job Function"
        onChange={(e) =>
          dispatch({ type: 'field', payload: { field: FieldNames.JOB_RESPONSIBILITY, value: e.target.value } })
        }
        value={jobResponsibility}
        options={jobFunctions.map((job) => ({ value: job }))}
        defaultOption="None Selected"
      />
    ),
    country: () => (
      <FormDropdown
        key="country"
        fieldName={FieldNames.COUNTRY}
        labelName="Country"
        onChange={(e) => dispatch({ type: 'field', payload: { field: FieldNames.COUNTRY, value: e.target.value } })}
        value={country}
        options={Object.keys(countries).map((countryCode) => ({
          value: countryCode,
          displayText: countries[countryCode],
        }))}
        defaultOption="Select Country"
      />
    ),
  };

  const extraFields = registrationExtraFields
    .filter((extraField) => extraFieldsMap[extraField] != null)
    .map((extraField) => extraFieldsMap[extraField]());

  return (
    <Container onSubmit={onSubmit} method="post" noValidate={onPrem}>
      <InviteBanner token={inviteToken} username={username} isFor="registration" retryInviteCheck={formSubmitted} />

      <HeaderLogo />
      {activationCode && (
        <Banner css={alertBanner} data-testid="bannerActivationCode">
          You&apos;re about to redeem your activation code. It will automatically be applied to your new organization.
        </Banner>
      )}
      {marketplaceRegistrationCookie && (
        <Banner variant={BannerVariant.Info} css={bannerStyle} data-testid="bannerMpRegistration">
          Create a MongoDB account to finish linking your {marketplaceRegistrationCookie.partnerType} account. If you
          already have an account,{' '}
          <Link to="/login" css={marketplaceLink}>
            log in
          </Link>{' '}
          now.
        </Banner>
      )}
      <H2 css={header}>Create your account</H2>
      {isGoogleAuthEnabled && (
        <>
          <GoogleAuthButton disabled={isSocialSignupDisabled} displayText="Sign up with Google" location={location} />
        </>
      )}
      {isGithubAuthEnabled && (
        <>
          <SocialAuthButton
            disabled={isSocialSignupDisabled}
            socialAuthProvider={SocialAuthProvider.GITHUB}
            location={location}
          />
        </>
      )}
      {isSocialAuthEnabled && <StyledLineDivider>or</StyledLineDivider>}
      {isRetiredUsername && (
        <Banner variant={BannerVariant.Danger} css={bannerStyle} data-testid="registrationErrorBanner">
          <strong>This email address cannot be used to create a MongoDB account. </strong>
          <br />
          Sign up for a new account with a different email address.
        </Banner>
      )}
      <FormInput
        fieldName="emailAddress"
        labelName={onPrem ? 'Username' : 'Email Address'}
        description="We recommend using your work email"
        autoComplete="username"
        type="email"
        onChange={(e) => dispatch({ type: 'field', payload: { field: FieldNames.USERNAME, value: e.target.value } })}
        value={username}
        regexValidation={fieldValidationMap[FieldNames.USERNAME]}
        hasError={isUsernameError}
        errorMessage={errorMessage}
        disabled={isInviteFlow}
      />
      <FormInput
        fieldName={FieldNames.FIRST_NAME}
        labelName="First Name"
        autoComplete="given-name"
        onChange={(e) => dispatch({ type: 'field', payload: { field: FieldNames.FIRST_NAME, value: e.target.value } })}
        value={firstName}
        regexValidation={fieldValidationMap[FieldNames.FIRST_NAME]}
        hasError={isFirstNameError}
        errorMessage={errorMessage}
      />
      <FormInput
        fieldName={FieldNames.LAST_NAME}
        labelName="Last Name"
        autoComplete="family-name"
        onChange={(e) => dispatch({ type: 'field', payload: { field: FieldNames.LAST_NAME, value: e.target.value } })}
        value={lastName}
        regexValidation={fieldValidationMap[FieldNames.LAST_NAME]}
        hasError={isLastNameError}
        errorMessage={errorMessage}
      />
      <PasswordInputWithValidationCriteria
        onChange={(e) => dispatch({ type: 'field', payload: { field: FieldNames.PASSWORD, value: e.target.value } })}
        value={password}
        hasError={isPasswordError}
        errorMessage={errorMessage}
        forNewUser
      />

      {/* Extra Registration Fields that vary by environment: Company, Country, Phone Number and Job Responsibility */}
      {extraFields}

      {/* the request to create a user is rejected by Backend if this field is filled in */}
      <FormInput
        fieldName="registrationComment"
        labelName="Comment"
        value={honeypot as string}
        onChange={(e) => dispatch({ type: 'field', payload: { field: FieldNames.HONEYPOT, value: e.target.value } })}
        optional
        hidden
      />
      {groupname && <FormInput labelName="Project Name" fieldName="groupName" value={groupname as string} disabled />}
      {orgname && <FormInput labelName="Organization Name" fieldName="orgName" value={orgname as string} disabled />}

      {!onPrem && (
        <CheckboxWrapper>
          <TermsOfServiceCheckbox
            checked={tos}
            onChange={(e) => dispatch({ type: 'field', payload: { field: FieldNames.TOS, value: e.target.checked } })}
          />
        </CheckboxWrapper>
      )}
      {reCaptchaEnabledRegistration && (
        <>
          <ReCaptcha
            ref={reCaptchaRef}
            callback={(token) => dispatch({ type: 'setCaptchaBoxChecked', payload: token.length > 0 })}
          />
          {captchaError && <ErrorMessage data-testid="error-message">{captchaError}</ErrorMessage>}
        </>
      )}
      {onPrem && !hasUsers && (
        <TermsOfServiceCheckbox
          checked={tos}
          variant="onprem"
          onChange={(e) => dispatch({ type: 'field', payload: { field: FieldNames.TOS, value: e.target.checked } })}
        />
      )}
      <footer>
        {errorMessage && !errorField && (
          <ErrorMessage data-testid="registration-error-message">{errorMessage}</ErrorMessage>
        )}
        {userRegistrationEnabled || inviteToken || activationCode ? (
          <Button variant="primary" disabled={isSubmitDisabled} size="default" css={submitButton} type="submit">
            Sign up
          </Button>
        ) : (
          <Tooltip
            justify={isSmallViewport ? 'middle' : 'start'}
            trigger={
              <div css={disabledButtonWrapper}>
                <Button variant="primary" disabled css={submitButtonWithoutTopMargins} size="default" type="submit">
                  Sign up
                </Button>
              </div>
            }
            triggerEvent="hover"
            id="submitTooltip"
          >
            User registration is currently disabled. Please contact your administrator for an invitation.
          </Tooltip>
        )}

        <FooterText>
          Have an account?
          <Link to={!onPrem ? `/login${search}` : '/login'}>&nbsp;Log in now</Link>
        </FooterText>
      </footer>
    </Container>
  );
}
